// src/pages/WarningsPage.js
import React, { useEffect, useState } from 'react'
import {
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  doc,
  where,
} from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { AlertTriangle, CheckCircle, RefreshCw, Filter } from 'lucide-react'

export default function WarningsPage() {
  const [warnings, setWarnings] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filterType, setFilterType] = useState('all')

  // 🔹 Ambil data dari Firestore
  const fetchWarnings = async (type = 'all') => {
    setRefreshing(true)
    try {
      let q
      if (type === 'all') {
        q = query(collection(db, 'warnings'), orderBy('tanggal', 'desc'))
      } else {
        q = query(
          collection(db, 'warnings'),
          where('mitraType', '==', type),
          orderBy('tanggal', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      const list = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      setWarnings(list)
    } catch (err) {
      console.error('❌ Gagal memuat data warnings:', err)
    }
    setRefreshing(false)
    setLoading(false)
  }

  useEffect(() => {
    fetchWarnings(filterType)
  }, [filterType])

  // 🔹 Tandai peringatan sebagai "dibaca"
  const markAsRead = async (id) => {
    try {
      await updateDoc(doc(db, 'warnings', id), { status: 'read' })
      setWarnings((prev) =>
        prev.map((w) => (w.id === id ? { ...w, status: 'read' } : w))
      )
    } catch (err) {
      console.error('❌ Gagal memperbarui status warning:', err)
    }
  }

  // 🔹 Format tanggal
  const formatDate = (ts) => {
    if (!ts) return '-'
    const date = ts.toDate ? ts.toDate() : new Date(ts)
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="p-6 text-gray-100 bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold flex items-center gap-2 text-yellow-400">
            <AlertTriangle size={20} /> Peringatan Mitra
          </h2>
          {refreshing && (
            <span className="text-gray-400 text-sm">(Memuat ulang...)</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* FILTER */}
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg px-3 py-1">
            <Filter size={14} className="text-gray-400 mr-1" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-transparent text-sm text-gray-200 outline-none cursor-pointer"
            >
              <option value="all">Semua Mitra</option>
              <option value="cleaner">Cleaner</option>
              <option value="therapist">Therapist</option>
            </select>
          </div>

          {/* TOMBOL REFRESH */}
          <button
            onClick={() => fetchWarnings(filterType)}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 text-sm"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Segarkan
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400">Memuat data peringatan...</p>
      ) : warnings.length === 0 ? (
        <p className="text-gray-500 italic">Tidak ada peringatan aktif.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-800 text-gray-300">
              <tr>
                <th className="px-3 py-2 text-left">Mitra ID</th>
                <th className="px-3 py-2 text-left">Jenis</th>
                <th className="px-3 py-2 text-left">Rating Akhir</th>
                <th className="px-3 py-2 text-left">Pesan</th>
                <th className="px-3 py-2 text-left">Tanggal</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {warnings.map((w) => (
                <tr
                  key={w.id}
                  className={`border-b border-gray-800 ${
                    w.status === 'unread'
                      ? 'bg-gray-850 hover:bg-gray-800'
                      : 'bg-gray-900 hover:bg-gray-800/70'
                  }`}
                >
                  <td className="px-3 py-2 text-blue-400 font-medium">
                    {w.mitraId}
                  </td>
                  <td className="px-3 py-2 capitalize text-gray-300">
                    {w.mitraType}
                  </td>
                  <td className="px-3 py-2 text-gray-100">
                    {w.ratingAkhir?.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-gray-300">{w.pesan}</td>
                  <td className="px-3 py-2 text-gray-400">
                    {formatDate(w.tanggal)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {w.status === 'read' ? (
                      <span className="flex items-center justify-center gap-1 text-green-400">
                        <CheckCircle size={14} /> Dibaca
                      </span>
                    ) : (
                      <button
                        onClick={() => markAsRead(w.id)}
                        className="text-yellow-400 hover:text-yellow-300 underline text-xs"
                      >
                        Tandai Dibaca
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
